import { useHeaderHeight } from "@react-navigation/elements";
import React, { createContext, useContext } from "react";
import { Platform } from "react-native";
import { useReanimatedKeyboardAnimation } from "react-native-keyboard-controller";
import Animated, {
  useDerivedValue,
  useAnimatedStyle,
  interpolate,
} from "react-native-reanimated";
import {
  useSafeAreaFrame,
  SafeAreaProvider,
} from "react-native-safe-area-context";

/**
 * ModalTabletAwareKeyboardStickyView
 * A specialized component that handles keyboard avoidance for iPad modals.
 *
 * Problem it solves:
 * On iPads, modals are displayed as centered overlays rather than full-screen views.
 * When the keyboard appears, the standard keyboard avoidance calculation assumes
 * full-screen content, causing incorrect positioning for modal content.
 *
 * How it works:
 * 1. Calculates the frame difference between the full screen (outer frame) and
 *    the modal's actual content area (inner frame)
 * 2. Uses this difference to determine the modal's bottom inset (assuming centered positioning)
 * 3. Adjusts the keyboard translation by this inset amount so content moves up
 *    only by the actual amount the keyboard overlaps the modal
 *
 * This ensures that modal content remains properly visible when the keyboard appears,
 * without over-scrolling or leaving unnecessary gaps.
 * How to use:
 * Wrap the root of the modal with <ModalTabletAwareKeyboardProvider></ModalTabletAwareKeyboardProvider>
 * Then wrap the content with <ModalTabletAwareKeyboardStickyView></ModalTabletAwareKeyboardStickyView>
 *
 */
const CTX = createContext<{
  frameDifference: number;
}>({
  frameDifference: 0,
});
export function ModalTabletAwareKeyboardProvider(props: {
  children: React.ReactNode;
}) {
  const frame = useSafeAreaFrame();

  return (
    <SafeAreaProvider>
      <ModalProviderInner outerFrame={frame}>
        {props.children}
      </ModalProviderInner>
    </SafeAreaProvider>
  );
}
function ModalProviderInner(props: {
  children: React.ReactNode;
  outerFrame: {
    height: number;
    width: number;
    x: number;
    y: number;
  };
}) {
  const frame = useSafeAreaFrame();

  return (
    <CTX.Provider
      value={{ frameDifference: props.outerFrame.height - frame.height }}
    >
      <>{props.children}</>
    </CTX.Provider>
  );
}

export const ModalTabletAwareKeyboardStickyView = (props: {
  children: React.ReactNode;
  offset?: {
    opened?: number;
    closed?: number;
  };
}) => {
  const anim = useReanimatedKeyboardAnimation();
  const keyboardHeight = useDerivedValue(() => {
    return -anim.height.value;
  });
  const offset = useDerivedValue(() =>
    interpolate(
      anim.progress.value,
      [0, 1],
      [props.offset?.closed ?? 0, props.offset?.opened ?? 0]
    )
  );

  const framedifference = useContext(CTX).frameDifference;

  // the modal will first use whatever y it has from the top of the

  const bottomOffScreenToBottomOfModal = framedifference / 2;

  const MIN_OFFSET_FROM_TOP_OF_MODAL_TO_SCREEN = 140;
  const modalPushUp =
    Platform.OS === "ios" && Platform.isPad
      ? Math.max(
          bottomOffScreenToBottomOfModal -
            MIN_OFFSET_FROM_TOP_OF_MODAL_TO_SCREEN,
          0
        )
      : 0;

  const headerHeight = useHeaderHeight();

  const ENABLE_TABLET_OFFSET =
    Platform.OS === "ios" &&
    Platform.isPad &&
    framedifference - headerHeight / 2 > MIN_OFFSET_FROM_TOP_OF_MODAL_TO_SCREEN;

  const tabletOffset = useDerivedValue(() =>
    ENABLE_TABLET_OFFSET
      ? bottomOffScreenToBottomOfModal + modalPushUp - headerHeight / 2
      : 0
  );

  const styles = useAnimatedStyle(() => ({
    transform: [
      {
        translateY:
          -Math.max(keyboardHeight.value - tabletOffset.value, 0) +
          offset.value,
      },
    ],
  }));

  return (
    <Animated.View style={styles} {...props}>
      {props.children}
    </Animated.View>
  );
};
